<template>
    <div class="map-locator-scale">
        <span
            v-if="displayLat && displayLng"
            class="coords is-hidden-mobile"
        >
            <span class="lat">
                {{ displayLat }}
            </span>
            <span class="lng">
                {{ displayLng }}
            </span>
        </span>
        <span class="scale-text">
            {{ scaleText }}
        </span>
        <span
            ref="scale"
            class="scale"
        />
    </div>
</template>

<script>
export default {
    name: 'MapLocatorScale',

    props: {
        map: {
            type: Object,
            required: true,
        },

        unitType: {
            type: String,
            required: false,
            default: 'metric',
        },
    },

    data: () => ({
        scaleText: '',
        displayLat: '',
        displayLng: '',
    }),

    beforeDestroy() {
        // remove event listeners
        this.map.off('zoom', this.updateScale);
        this.map.off('mousemove', this.onMouseMove);
    },

    mounted() {
        // update scale on initial load
        this.updateScale();

        // update scale for every map zoom event
        this.map.on('zoom', this.updateScale);

        // update coordinates of mouse on map
        this.map.on('mousemove', this.onMouseMove);
    },

    methods: {
        /**
         * Handle mousemove map events
         *
         * @param {object} ev - mapbox mousemove event
         */
        onMouseMove(ev) {
            const { lng, lat } = ev.lngLat;

            if (lat && lng) {
                this.displayLat = `${lat.toFixed(4)}°N`;
                this.displayLng = `${lng.toFixed(4)}°W`;
            } else {
                this.displayLat = '';
                this.displayLng = '';
            }
        },

        /**
         * Set the value and width for the scale.
         *
         * @param {number} width - width of the scale
         * @param {string} scaleText - Text to be displayed alongside the scale
         */
        setScaleValues(width, scaleText) {
            this.$refs.scale.style.width = width;
            this.scaleText = scaleText;
        },

        /**
         * Updates the scale for the locator control - copied from
         * <https://github.com/mapbox/mapbox-gl-js/blob/d2f1eea030568d421906e31f64bb8ad2803ae135/src/ui/control/scale_control.js>
         * and slightly modified to suit our needs - we don't need nautical scales.
         */
        updateScale() {
            // A horizontal scale is imagined to be present at center of the map
            // container with maximum length (Default) as 100px.
            // Using spherical law of cosines approximation, the real distance is
            // found between the two coordinates.

            const maxWidth = 100;

            const y = this.map._container.clientHeight / 2;
            const left = this.map.unproject([0, y]);
            const right = this.map.unproject([maxWidth, y]);
            const maxMeters = left.distanceTo(right);
            // The real distance corresponding to 100px scale length is rounded off to
            // near pretty number and the scale length for the same is found out.
            if (this.unitType === 'imperial') {
                const maxFeet = 3.2808 * maxMeters;
                if (maxFeet > 5280) {
                    const maxMiles = maxFeet / 5280;
                    this.setScale(maxWidth, maxMiles, 'mi');
                } else {
                    this.setScale(maxWidth, maxFeet, 'ft');
                }
            } else if (maxMeters >= 1000) {
                this.setScale(maxWidth, maxMeters / 1000, 'km');
            } else {
                this.setScale(maxWidth, maxMeters, 'm');
            }
        },

        /**
         * helper function for setting the scale width and text
         *
         * @param {number} maxWidth - the maximum width of the scale ruler item, in pixels
         * @param {number} maxDistance - a distance derived from a map measurement
         * @param {string} unit - the unit to measure in
         */
        setScale(maxWidth, maxDistance, unit) {
            const distance = this.getRoundNum(maxDistance);
            const ratio = distance / maxDistance;
            this.setScaleValues(`${maxWidth * ratio}px`, `${distance} ${unit} `);
        },

        /**
         * select a nice number for showing on a scale
         *
         * @param {number} num - the number to round to a specific value.
         * @returns {number} - A number like 1000, 500, 30, etc.
         */
        getRoundNum(num) {
            const pow10 = 10 ** ((`${Math.floor(num)}`).length - 1);
            let d = num / pow10;
            const limits = [10, 5, 3, 2, 1];
            d = limits.find((limit) => d >= limit);
            // d can be undefined if all the limits are larger, this happens when we zoom in so far
            // that the scale defined with the maxwidth is under a meter.
            if (!d) {
                d = this.getDecimalRoundNum(d);
            }
            return pow10 * d;
        },

        /**
         * Gets a nice rounded decimal number - so if you supply it something like 0.3948284827482 you get 0.4.
         * I got this from mapbox's scale control source
         *
         * @param {number} d - the decimal value you want rounded
         * @returns {number} - a rounded decimal value.
         */
        getDecimalRoundNum(d) {
            const multiplier = 10 ** (Math.ceil(-Math.log(d) / Math.LN10));
            return Math.round(d * multiplier) / multiplier;
        },
    },
};
</script>

<style lang="scss">
.map-locator-scale {
    position: absolute;
    left: 10px;
    bottom: 35px;
    display: flex;
    flex-direction: row;

    color: rgba(255, 255, 255, 1);
    background-color: rgba(0, 0, 0, 0.5);
    font-size: 0.75em;
    padding: 0.5em 1em;

    .coords {
        padding-right: 0.6em;

        .lat {
            padding-right: 0.5em;
        }
    }

    .scale-text {
        padding-right: 0.25em;
    }

    span.scale {
        align-self: center;
        padding-right: 0;
        display: inline-block;
        width: 100px;
        box-sizing: border-box;
        border: 1px solid rgba(255, 255, 255, 1);
        height: 0.75em;
        border-top: 0;
    }
}
</style>
